import { numberWithCommas } from './helpers'

export default class MECAnimations {
  constructor(plugins = {}) {
    this.gsap = plugins.gsap;
    this.ScrollTrigger = plugins.ScrollTrigger;
    this.ScrollSmoother = plugins.ScrollSmoother;
    this.SplitText = plugins.SplitText;
    this.Lenis = plugins.Lenis;

    this.elements = {};
    this.timelines = {};
    this.scrollTriggers = [];
    this.lenis = null;
    this.isInitialized = false;
    this.matchMedia = null; // Store matchMedia instance for cleanup
  }

  cacheElements() {
    this.elements = {
      header: document.querySelector('#header'),
      // Hero sections
      heroSection: document.querySelector('.hero-section'),
      heroContent: document.querySelector('.hero-content'),
      heroBgWrapper: document.querySelector('.hero-bg-img-wrapper'),
      heroBgFig: document.querySelector('.hero-bg-img-wrapper figure'),
      heroBrandWrapper: document.querySelector('.hero-texts-wrapper'),
      heroBrandWrapperContent: document.querySelector('.hero-texts-wrapper-light'),

      // Second hero section
      secondHeroSection: document.querySelector('.hero-section-imgs'),
      heroImgsWrapper: document.querySelector('.imgs-blocks-wrapper'),
      brndHeroImgsWrapper: document.querySelector('.brand-text-imgs-wrapper'),
      secondHeroSectionBgImg: document.querySelector('.hero-imgs-bg-img-wrapper'),

      // Bio section
      bioSection: document.querySelector('.bio-section'),
      bioSectionContent: document.querySelector('.bio-section .bio-content'),
      bioSectionIntroImg: document.querySelector('.bio-section .bio-intro-img'),
      bioSectionContentRow: document.querySelector('.bio-section .bio-content-row'),

      bioMobileSection: document.querySelector('.sm-bio-section'),
      bioMobileSectionItems: document.querySelectorAll('.sm-bio-section .--item'),

      // Projects section
      projectsSection: document.querySelector('.projects-section'),
      projectsHeading: document.querySelector('.projects-section-heading-wrapper .--heading'),

      // Menu
      heroMenu: document.querySelector('.hero-menu'),
    };

    // Cache child elements
    if (this.elements.brndHeroImgsWrapper) {
      this.elements.heroImgDarkWrapper = this.elements.brndHeroImgsWrapper.querySelector('.dark-logo-img-wrapper');
      this.elements.heroImgLightWrapper = this.elements.brndHeroImgsWrapper.querySelector('.light-logo-img-wrapper');
    }

    if (this.elements.secondHeroSectionBgImg) {
      this.elements.secondHeroSectionBgImgFig = this.elements.secondHeroSectionBgImg.querySelector('figure');
    }

    if (this.elements.heroMenu) {
      this.elements.heroMenuItems = this.elements.heroMenu.querySelectorAll("h3");
    }
  }

  initLenis() {
    this.lenis = new this.Lenis({ autoRaf: true });

    // Store ticker function reference for proper cleanup
    this.lenisTickerCallback = (time) => {
      this.lenis.raf(time * 1000);
    };

    // Throttle ScrollTrigger updates for better performance
    let updateTimeout;
    this.throttledScrollUpdate = () => {
      if (updateTimeout) return;
      updateTimeout = requestAnimationFrame(() => {
        this.ScrollTrigger.update();
        updateTimeout = null;
      });
    };

    // Sync with ScrollTrigger
    this.lenis.on('scroll', this.throttledScrollUpdate);

    // Drive via GSAP ticker
    this.gsap.ticker.add(this.lenisTickerCallback);
    this.gsap.ticker.lagSmoothing(0);
    this.lenis.stop(); // Start stopped, will be started after preloader
  }

  initScrollTriggerDefaults() {
    this.ScrollTrigger.defaults({
      anticipatePin: 1,
      markers: false,
      // fastScrollEnd: true,
      ease: "linear",
      // preventOverlaps: true,
    });
  }

  initHeroAnimations() {
    if (!this.elements.heroSection) return;

    // Hero brand wrapper animation
    const brandWrapperTrigger = this.gsap.to(this.elements.heroBrandWrapperContent, {
      clipPath: "inset(0% 0% 0% 0% round 0)",
      ease: "linear",
      scrollTrigger: {
        trigger: this.elements.heroSection,
        start: "top top",
        end: `${this.elements.heroBrandWrapperContent.clientHeight * 2 - 80}px top`,
        scrub: 1.2,
        markers: false,
      },
    });

    // Main hero timeline
    this.timelines.hero = this.gsap.timeline({
      scrollTrigger: {
        trigger: this.elements.heroSection,
        start: "top top",
        end: "bottom bottom",
        scrub: 1.2,
        markers: false,
      }
    });

    // Add animations to timeline without individual ScrollTriggers
    this.timelines.hero
      .to(this.elements.heroBgFig, {
        height: "100%",
        borderRadius: 0,
        duration: 0.3,
      })
      .to(this.elements.heroBrandWrapper, {
        marginTop: "80px",
        duration: 0.3,
      }, "<")
      .to(this.elements.heroBgWrapper, {
        height: "100%",
        duration: 0.3,
      }, "<");

    this.scrollTriggers.push(brandWrapperTrigger.scrollTrigger);
  }

  initSecondHeroAnimations() {
    if (!this.elements.secondHeroSection) return;

    // Preloader timeline (non-scroll triggered)
    this.timelines.preloader = this.gsap.timeline();

    this.timelines.preloader
      // .set(document.querySelector(".home"), { visibility: "visible" })
      .add(() => {
        this.lenis.scrollTo(0, { immediate: true });
      })
      .to(this.elements.secondHeroSection, { opacity: 1, duration: 0.5, delay: 0.2 })
      .to(document.querySelector(".logo-collapsed a img"), { opacity: 1, duration: 1, delay: 0.5 }, 0)
      .from(this.elements.secondHeroSectionBgImg, { right: "100%", duration: 2.5, delay: 0.5 }, 0)
      .from(document.querySelector(".--loading-num"), {
        textContent: 0, snap: { textContent: 1 }, stagger: {
          each: 1.0,
          onUpdate: function () {
            this.targets()[0].innerHTML = numberWithCommas(Math.ceil(this.targets()[0].textContent));
          },
        }, duration: 2.5, delay: 0.5
      }, 0)
      .to(document.querySelector(".--loading-num"), { opacity: 0, filter: "blur(10px)", duration: 0.3, delay: 3.5 }, 0)
      .to(this.elements.secondHeroSectionBgImg, { top: "65%", duration: 0.5, delay: 3, ease: "power2.inOut" }, 0)
      .from(".dark-logo-img-wrapper .hero-values > h3", { y: "100%", duration: 0.5, stagger: 0.2, delay: 3.5 }, 0)
      .from(".light-logo-img-wrapper .hero-values > h3", { y: "100%", duration: 0.5, stagger: 0.2, delay: 3.5 }, 0)
      .from(".light-logo-img-wrapper figure img, .dark-logo-img-wrapper figure img", { y: -40, opacity: 0, duration: 0.5, delay: 3 }, 0)
      .add(() => {
        if (document.readyState === 'complete') {
          this.lenis.start();
          document.body.style.overflow = "auto";
        }
      })
      .to(".ct-header-socials .ct-social-box a", { opacity: 1, x: 0, duration: 1.5, delay: 4, stagger: 0.1 }, 0)
      .to(".logo-collapsed a img", { height: "100%", width: "100%", duration: 1.2, ease: "power2.inOut", delay: 3 }, 0)
      // .to(".outline-logo-img-wrapper", { opacity: 0, duration: 1.5, ease: "power4.inOut" }, "<-=1")
      .to(document.querySelectorAll("#menu-primary a"), { y: 0, duration: 0.3, stagger: 0.1, delay: 4 }, 0);

    // Scroll-triggered timeline
    this.timelines.secondHero = this.gsap.timeline({
      scrollTrigger: {
        trigger: this.elements.secondHeroSection,
        start: "top top",
        end: `${window.innerHeight * 1.5}px top`,
        scrub: 1.2,
        markers: false,
      }
    });

    this.gsap.fromTo(this.elements.header, {
      "--header-color": "rgba(0,0,0,1)",
      duration: 0.2,
    }, {
      "--header-color": "rgba(255,255,255,1)",
      ease: "linear",
      duration: 0.2,
      scrollTrigger: {
        trigger: this.elements.secondHeroSection,
        start: `${window.innerHeight * 0.3} top`,
        end: `+=${window.innerHeight + 100}px`,
        toggleActions: "play reverse play reverse",
        scrub: false,
        markers: false,
      }
    })

    this.timelines.secondHero
      .fromTo(this.elements.secondHeroSectionBgImg,
        { top: "65%" },
        {
          top: 0,
          duration: 0.3,
          immediateRender: false,
          overwrite: "auto"
        }
      )
      .to(this.elements.brndHeroImgsWrapper.querySelectorAll("figure,.hero-values"), {
        y: "-25vh",
        ease: "power1.inOut",
        duration: 0.3,
      }, "<")
      .addLabel("logosUp")
      .to(this.elements.heroImgDarkWrapper, {
        clipPath: `inset(0% 0% 100% 0%)`,
        duration: 0.3,
      }, "<")
      .to(this.elements.heroImgLightWrapper, {
        clipPath: `inset(0% 0% 35% 0%)`,
        duration: 0.3,
      }, "<")
      .to(this.elements.secondHeroSectionBgImgFig, {
        scaleX: 1.05,
        scaleY: 1.06,
        // borderRadius: 12,
        duration: 0.5,
      }, "<+=0.3")
      .addLabel("bgZoom")
      .to(this.elements.heroImgsWrapper, {
        scale: 0.8,
        y: "-10%",
        ease: "linear",
        duration: 0.3,
      }, ">");
  }

  initCategoriesSectionAnimations() {
    return;
    const sectionElement = document.querySelector(".categories-section")
    const sectionContentWrapper = document.querySelector(".categories-section-content")

    if (!sectionElement || !sectionContentWrapper) return;

    // Main bio timeline
    this.timelines.categoriesSection = this.gsap.timeline({
      duration: 10,
      scrollTrigger: {
        trigger: sectionElement,
        start: "top top",
        end: "bottom bottom",
        scrub: 0.5,
        markers: false,
      }
    });

    // this.gsap.fromTo(this.elements.header, {
    //   "--header-color": "rgba(255,255,255,1)",
    //   duration: 0.2,
    // }, {
    //   "--header-color": "rgba(0,0,0,1)",
    //   ease: "linear",
    //   duration: 0.2,
    //   scrollTrigger: {
    //     trigger: sectionElement,
    //     start: "-40px top",
    //     toggleActions: "play none none reverse",
    //     scrub: false,
    //     markers: false,
    //   }
    // })

    // local elements
    const logoWrapper = sectionElement.querySelector(".cs-logo-wrapper")
    const logoParagraph = sectionElement.querySelector(".cs-f-p-wrapper")

    this.timelines.categoriesSection
      .to(logoWrapper, {
        // opacity: 1,
        y: -100,
        duration: 0.2 // very short duration relative to scroll
      }, 0)
      .to(logoParagraph.querySelector("h6"), {
        opacity: 1,
        y: -200,
        duration: 0.2 // very short duration relative to scroll
      }, 0)
      .to(logoWrapper.querySelector("figure:not(.--placeholder)"), {
        // opacity: 1,
        left: "-75vw",
        filter: "grayscale(1)",
        opacity: 0.1,
        duration: 0.2 // very short duration relative to scroll
      }, 0.1)
      .to(logoParagraph.querySelector("h6"), {
        opacity: 0,
        y: "-=100",
        duration: 0.2 // very short duration relative to scroll
      }, "<+=0.05");
  }

  initBioAnimations() {
    if (!this.elements.bioSection) return;

    // Logo toggle
    this.gsap.from(document.querySelector(".logo-collapsed a"), {
      scrollTrigger: {
        trigger: this.elements.bioSection,
        start: "20% top",
        markers: false,
        toggleActions: "play none none none",
        toggleClass: {
          targets: ".logo-collapsed a",
          className: "is-active"
        }
      }
    });

    // Main bio timeline
    this.timelines.bio = this.gsap.timeline({
      scrollTrigger: {
        trigger: this.elements.bioSection,
        start: "top top",
        end: "bottom bottom",
        scrub: 1.2,
        markers: false,
      }
    });

    this.timelines.bio
      .to(this.elements.bioSection.querySelector(".who-is-mec-heading"), {
        y: "0%",
        opacity: 1,
        duration: 0.2,
      }, 0.35)
      .to(this.elements.bioSectionIntroImg, {
        left: 0, right: 0, top: 0, bottom: 0,
        ease: "power1.out",
        duration: 0.23,
      }, 0)
      .to(this.elements.bioSectionContentRow.querySelector(".bio-content-row-item:nth-child(2)"), {
        x: "0%", ease: "power1.inOut", duration: 0.33,
      }, 0.3)
      .to(this.elements.bioSectionContentRow.querySelector(".bio-content-row-item:nth-child(3)"), {
        x: "0%", ease: "power1.inOut", duration: 0.33,
      }, 0.6)
      .to(this.elements.bioSectionContentRow.querySelector(".bio-content-row-item:nth-child(4)"), {
        x: "0%", ease: "power1.inOut", duration: 0.33,
      }, 0.9)
      .to(this.elements.bioSectionIntroImg, {
        scale: 1.2,
        filter: "blur(6px) grayscale(1)",
        ease: "power1.inOut",
        duration: 0.72,
      }, 0.43);

    // Individual bio items
    this.initBioItems();
  }

  initBioItems() {
    const bioItems = this.elements.bioSectionContentRow.querySelectorAll(".bio-content-row-item:not(.--placeholder)");

    this.ScrollTrigger.batch(bioItems, {
      onEnter: (elements) => {
        elements.forEach((element, index) => {
          this.gsap.from(element.querySelector(".--cover-wrapper img"), {
            scale: 1.4,
            duration: 1,
            ease: "power2.inOut",
          });

          this.gsap.from(element.querySelector(".--heading-wrapper"), {
            x: "120%",
            duration: 1,
            ease: "power3.out",
          });

          this.gsap.to(element.querySelector(".--content-wrapper p"), {
            "--stop1": "100%",
            "--stop2": "140%",
            duration: 1,
            ease: "power3.out",
            delay: 0.5,
          });
        });
      },
      once: true,
    });
  }

  initProjectsAnimations() {
    if (!this.elements.projectsSection) return;

    // Projects heading split text
    if (this.elements.projectsHeading) {
      const split = new this.SplitText(this.elements.projectsHeading, {
        type: "words",
        autoSplit: true,
      });

      this.gsap.from(split.words, {
        y: 100,
        filter: "blur(10px)",
        autoAlpha: 0,
        stagger: 0.05,
        scrollTrigger: {
          trigger: this.elements.projectsHeading,
          start: "-50% center",
          end: "+=200px",
          markers: false,
          toggleActions: "play none none reverse",
        }
      });
    }

    // Cache project elements
    this.elements.projectRows = this.gsap.utils.toArray('.projects-section-content .projects-section-inner-row');
    this.elements.horizontalWrapper = document.querySelector(".projects-horizontal-wrapper");

    // Initialize horizontal scroll if wrapper exists
    if (this.elements.horizontalWrapper && this.elements.projectRows.length > 1) {
      this.initHorizontalProjectScroll();
    }

    // Initialize individual project items
    this.initProjectItems(this.elements.projectRows);
  }

  initHorizontalProjectScroll() {
    // Calculate total width needed for horizontal scroll
    const totalWidth = this.elements.projectRows.length * 100;

    // Main horizontal scroll timeline
    this.timelines.projectsHorizontal = this.gsap.timeline({
      scrollTrigger: {
        trigger: this.elements.projectsSection,
        start: "top top",
        end: "bottom bottom",
        scrub: 1.2,
        markers: false,
        anticipatePin: 1,
      }
    });

    // Horizontal movement - keep your direction (left scroll)
    this.timelines.projectsHorizontal.fromTo(this.elements.horizontalWrapper, {
      x: "-25vw"
    }, {
      x: `${totalWidth - 100}vw`, // This moves content LEFT as you scroll down
      ease: "none", // Use "none" for smooth scrub - this is important for performance
    });
  }

  initProjectItems(projectRows) {
    projectRows.forEach((proj, index) => {
      // Cache project elements
      const projectElements = {
        projName: proj.querySelectorAll(".--project-name"),
        projLocation: proj.querySelector(".--project-location"),
        projYear: proj.querySelector(".--project-year"),
        projGallery: proj.querySelectorAll(".proj-gallery > figure"),
        projImages: proj.querySelectorAll(".proj-gallery > figure img"),
        projContent: proj.querySelector(".project-content"),
        projTitle: proj.querySelector(".project-title"),
      };

      // Create individual project timeline with proper ScrollTrigger for reversible animations
      const projectTimeline = this.gsap.timeline({
        scrollTrigger: {
          trigger: proj,
          start: index == (projectRows.length - 1) ? "50% 30%" : "right 50%", // Start when project is centered
          end: "right 150%", // Add end point for proper reverse
          horizontal: true,
          markers: false,
          containerAnimation: this.timelines.projectsHorizontal,
          toggleActions: "play none none reverse", // This enables reverse on scroll back
        }
      });

      // Store timeline reference
      this.timelines[`project_${index}`] = projectTimeline;

      // Project details fade in
      if (projectElements.projName.length || projectElements.projLocation || projectElements.projYear) {
        const detailElements = [
          ...Array.from(projectElements.projName),
          projectElements.projLocation,
          projectElements.projYear
        ].filter(Boolean);

        projectTimeline.from(detailElements, {
          y: 50,
          opacity: 0,
          duration: 0.3,
          stagger: 0.05,
          ease: "power2.out",
        }, 0);
      }

      // Project content reveal
      if (projectElements.projContent) {
        projectTimeline.from(projectElements.projContent, {
          y: 100,
          opacity: 0,
          duration: 0.4,
          ease: "power2.out",
        }, 0.1);
      }

      // Gallery reveal animation
      if (projectElements.projGallery.length) {
        projectTimeline.fromTo(projectElements.projGallery, {
          clipPath: "inset(0% 0% 100% 0% round 0)",
          filter: "grayscale(1) brightness(2)",
        }, {
          clipPath: "inset(0% 0% 0% 0% round 0)",
          filter: "grayscale(0) brightness(1)",
          duration: 0.6,
          stagger: -0.1,
          ease: "power4.out",
        }, 0.2);

        // Initialize optimized parallax for this project
        this.initProjectParallax(projectElements.projGallery, proj, index);
      }
    });
  }

  initProjectParallax(galleryFigures, projectContainer, projectIndex) {
    if (!galleryFigures.length) return;

    // Use single ScrollTrigger for all figures in this project (much more efficient)
    const parallaxTimeline = this.gsap.timeline({
      scrollTrigger: {
        trigger: projectContainer,
        start: "center center",
        end: "left center",
        horizontal: true,
        scrub: true,
        markers: false, // Remove debug markers
        containerAnimation: this.timelines.projectsHorizontal,
      }
    });

    // Different parallax speeds for variety
    const parallaxSpeeds = [0.5, -0.3, 0.8, -0.6, 0.4];

    // Animate all figures in single timeline instead of creating multiple ScrollTriggers
    galleryFigures.forEach((figure, figureIndex) => {
      return;
      const imgs = figure.querySelectorAll('img');
      if (!imgs.length) return;

      const speed = parallaxSpeeds[figureIndex % parallaxSpeeds.length];

      // Add to single timeline instead of creating multiple ScrollTriggers
      parallaxTimeline
        .to(figure, {
          y: `${speed * 150}px`,
          x: `${speed * (75)}px`,
          stagger: -0.3,
          duration: 1,
          ease: "none",
        }, 0) // Start all at same time
        .to(figure, {
          // scale: 1 - Math.abs(speed) * 0.15,
          duration: 1,
          ease: "none",
        }, 0);
    });

    // Store single timeline reference instead of multiple
    this.timelines[`parallax_${projectIndex}`] = parallaxTimeline;
  }

  // ========================================
  // MOBILE/TABLET ANIMATIONS (Simplified)
  // ========================================

  initMobileHeroAnimations() {
    if (!this.elements.heroSection) return;

    console.log("Initializing mobile hero animations");

    // Simple fade-in for hero content (no complex scrolling animations)
    this.timelines.heroMobile = this.gsap.timeline({
      scrollTrigger: {
        trigger: this.elements.secondHeroSection,
        start: "top top",
        end: "bottom bottom",
        scrub: 1.2,
        markers: false,
      }
    });

    this.timelines.heroMobile
      .from(this.elements.secondHeroSection, {
        opacity: 0,
        y: 50,
        duration: 1,
        ease: "power2.out",
        delay: 0.5
      })
      .from(this.elements.heroBgFig, {
        opacity: 0,
        scale: 1.1,
        duration: 1.5,
        ease: "power2.out",
      }, "-=0.5");
  }

  initMobileSecondHeroAnimations() {
    if (!this.elements.secondHeroSection) return;

    // console.log("Initializing mobile second hero animations");

    // Simplified preloader for mobile (faster, less complex)
    this.timelines.preloaderMobile = this.gsap.timeline();

    this.timelines.preloaderMobile
      // .set(document.querySelector(".home"), { visibility: "visible" })
      .add(() => {
        this.lenis.scrollTo(0, { immediate: true });
      })
      .to(this.elements.secondHeroSection, { opacity: 1, duration: 0.5, delay: 0.2 })
      .to(document.querySelector(".logo-collapsed a img"), { opacity: 1, duration: 1, delay: 0.5 }, 0)
      .from(this.elements.secondHeroSectionBgImg, { right: "100%", duration: 2.5, delay: 0.5 }, 0)
      .from(document.querySelector(".--loading-num"), {
        textContent: 0, snap: { textContent: 1 }, stagger: {
          each: 1.0,
          onUpdate: function () {
            this.targets()[0].innerHTML = numberWithCommas(Math.ceil(this.targets()[0].textContent));
          },
        }, duration: 2.5, delay: 0.5
      }, 0)
      .to(document.querySelector(".--loading-num"), { opacity: 0, filter: "blur(10px)", duration: 0.3, delay: 3.5 }, 0)
      .to(this.elements.secondHeroSectionBgImg, { top: "55%", duration: 0.5, delay: 3, ease: "power2.inOut" }, 0)
      .from(".dark-logo-img-wrapper .hero-values > h3", { y: "100%", duration: 0.5, stagger: 0.2, delay: 3.5 }, 0)
      .from(".light-logo-img-wrapper .hero-values > h3", { y: "100%", duration: 0.5, stagger: 0.2, delay: 3.5 }, 0)
      .from(".light-logo-img-wrapper figure img, .dark-logo-img-wrapper figure img", { y: -40, opacity: 0, duration: 0.5, delay: 3 }, 0)
      .add(() => {
        if (document.readyState === 'complete') {
          this.lenis.start();
          document.body.style.overflow = "auto";
        }
      })
      .from(".ct-header-socials .ct-social-box a", { opacity: 0, x: -10, duration: 1.5, delay: 4, stagger: 0.1 }, 0)
      // .to(".logo-collapsed a img", { height: "100%", width: "100%", duration: 1.2, ease: "power2.inOut", delay: 3 }, 0)
      // .to(".outline-logo-img-wrapper", { opacity: 0, duration: 1.5, ease: "power4.inOut" }, "<-=1")
      .to(document.querySelectorAll("#menu-primary a"), { y: 0, duration: 0.3, stagger: 0.1, delay: 4 }, 0);

    // Scroll-triggered timeline
    this.timelines.secondHero = this.gsap.timeline({
      scrollTrigger: {
        trigger: this.elements.secondHeroSection,
        start: "top top",
        end: `${window.innerHeight * 1.5}px top`,
        scrub: 1.2,
        markers: false,
      }
    });

    this.gsap.fromTo(this.elements.header, {
      "--header-color": "rgba(0,0,0,1)",
      duration: 0.2,
    }, {
      "--header-color": "rgba(255,255,255,1)",
      ease: "linear",
      duration: 0.2,
      scrollTrigger: {
        trigger: this.elements.secondHeroSection,
        start: `${window.innerHeight * 0.3} top`,
        end: `+=${window.innerHeight + 100}px`,
        toggleActions: "play reverse play reverse",
        scrub: false,
        markers: false,
      }
    })

    this.timelines.secondHero
      .fromTo(this.elements.secondHeroSectionBgImg,
        { top: "55%" },
        {
          top: 0,
          duration: 0.3,
          immediateRender: false,
          overwrite: "auto"
        }
      )
      .to(this.elements.brndHeroImgsWrapper.querySelectorAll("figure,.hero-values"), {
        y: "-25vh",
        ease: "power1.inOut",
        duration: 0.3,
      }, "<")
      .addLabel("logosUp")
      .to(this.elements.heroImgDarkWrapper, {
        clipPath: `inset(0% 0% 100% 0%)`,
        duration: 0.3,
      }, "<")
      .to(this.elements.heroImgLightWrapper, {
        clipPath: `inset(0% 0% 45% 0%)`,
        duration: 0.3,
      }, "<")
      .to(this.elements.secondHeroSectionBgImgFig, {
        scaleX: 1.05,
        scaleY: 1.06,
        // borderRadius: 12,
        duration: 0.5,
      }, "<+=0.3")
      .addLabel("bgZoom")
      .to(this.elements.heroImgsWrapper, {
        scale: 0.8,
        y: "-10%",
        ease: "linear",
        duration: 0.3,
      }, ">");

  }

  initMobileBioAnimations() {
    if (!this.elements.bioSection) return;

    // console.log("Initializing mobile bio animations");

    const bioItems = this.elements.bioMobileSection.querySelectorAll(".--item");

    // Store individual ScrollTriggers for cleanup
    this.bioScrollTriggers = [];

    bioItems.forEach((item, index) => {
      const paragraph = item.querySelector(".bb-content .bb-paragraph");
      if (!paragraph) return;

      // Split text
      new this.SplitText(paragraph, {
        type: "lines",
        linesClass: "split-line-wrapped",
        mask: "lines",
      });

      const splitElements = item.querySelectorAll(".split-line-wrapped");
      if (splitElements.length === 0) return;

      // Set initial state
      this.gsap.set(splitElements, {
        y: 100,
        opacity: 0,
        transformOrigin: "center bottom"
      });

      // Create timeline for entrance animation
      const itemTimeline = this.gsap.timeline({ paused: true });

      itemTimeline.to(splitElements, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.1
      });

      // Track if element has been animated
      let hasBeenAnimated = false;

      // Entrance ScrollTrigger - only triggers once when entering
      const entranceScrollTrigger = this.ScrollTrigger.create({
        trigger: item,
        start: "top 85%",
        end: "bottom 85%",
        markers: false,

        onEnter: () => {
          if (!hasBeenAnimated) {
            itemTimeline.restart();
            hasBeenAnimated = true;
          }
        },

        // No other callbacks - just entrance
        refreshPriority: -1
      });

      // Reset ScrollTrigger - only when scrolling far up (200% above)
      const resetScrollTrigger = this.ScrollTrigger.create({
        trigger: item,
        start: "top 200%", // 200% above the element
        end: "top 100%",   // Until element is 100% above
        markers: false,

        onEnterBack: () => {
          // Reset when scrolling back up to this far point
          this.gsap.set(splitElements, {
            y: 100,
            opacity: 0
          });
          hasBeenAnimated = false; // Allow re-animation
        },

        refreshPriority: -2
      });

      // Store references
      this.bioScrollTriggers.push(entranceScrollTrigger, resetScrollTrigger);
      this.timelines[`bioMobile_${index}`] = itemTimeline;
    });
  }

  initMobileProjectsAnimations() {
    if (!this.elements.projectsSection) return;

    // console.log("Initializing mobile projects animations");

    // Cache project elements (no horizontal scroll on mobile)
    this.elements.projectRows = this.gsap.utils.toArray('.projects-section-content .projects-section-inner-row');

    // Store individual ScrollTriggers for cleanup
    this.projectScrollTriggers = [];

    // Handle projects heading with seamless animation
    if (this.elements.projectsHeading) {
      // Set initial state for heading
      this.gsap.set(this.elements.projectsHeading, {
        opacity: 0,
        y: 50
      });

      // Create timeline for heading entrance
      const headingTimeline = this.gsap.timeline({ paused: true });

      headingTimeline.to(this.elements.projectsHeading, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power2.out"
      });

      // Track if heading has been animated
      let headingAnimated = false;

      // Entrance ScrollTrigger for heading
      const headingEntranceScrollTrigger = this.ScrollTrigger.create({
        trigger: this.elements.projectsHeading,
        start: "top 85%",
        end: "bottom 85%",
        markers: false,

        onEnter: () => {
          if (!headingAnimated) {
            headingTimeline.restart();
            headingAnimated = true;
          }
        },

        refreshPriority: -1
      });

      // Reset ScrollTrigger for heading - when far above (200%)
      const headingResetScrollTrigger = this.ScrollTrigger.create({
        trigger: this.elements.projectsHeading,
        start: "top 200%", // 200% above the element
        end: "top 100%",   // Until element is 100% above
        markers: false,

        onEnterBack: () => {
          // Reset when scrolling back up to this far point
          this.gsap.set(this.elements.projectsHeading, {
            opacity: 0,
            y: 50
          });
          headingAnimated = false; // Allow re-animation
        },

        refreshPriority: -2
      });

      // Store heading ScrollTriggers
      this.projectScrollTriggers.push(headingEntranceScrollTrigger, headingResetScrollTrigger);
      this.timelines.projectsHeadingMobile = headingTimeline;
    }

    // Handle individual project items with seamless animation
    this.elements.projectRows.forEach((proj, index) => {
      // Set initial state for project
      this.gsap.set(proj, {
        opacity: 0,
        y: 50
      });

      // Set initial state for project gallery
      const projGallery = proj.querySelectorAll(".proj-gallery > figure");
      if (projGallery.length) {
        this.gsap.set(projGallery, {
          opacity: 0,
          y: 30
        });
      }

      // Split project name
      const projName = proj.querySelector(".--project-name");
      if (projName) {
        const splitName = new this.SplitText(projName, {
          type: "chars",
          autoSplit: true,
          charsClass: "proj-title-words"
        });
        this.gsap.set(splitName.chars, {
          y: 50,
          opacity: 0
        });
      }

      const projTitleWords = proj.querySelectorAll(".proj-title-words");
      if (projTitleWords.length) {
        this.gsap.set(projTitleWords, {
          y: 50,
          opacity: 0
        });
      }

      const projMeta = proj.querySelectorAll(".--project-location, .--project-year");
      if (projMeta.length) {
        this.gsap.set(projMeta, {
          y: 50,
          opacity: 0
        });
      }

      // Create timeline for this project's entrance animation
      const projectTimeline = this.gsap.timeline({ paused: true });

      // Add project fade-in to timeline
      projectTimeline.to(proj, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power2.out"
      });

      // Add gallery animation to timeline if gallery exists
      if (projGallery.length) {
        projectTimeline.to(projGallery, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "power2.out"
        }, "-=0.8"); // Start 0.8s before the end of project animation
      }

      if (projName) {
        projectTimeline.to(projName.querySelectorAll(".proj-title-words"), {
          y: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.05,
          ease: "power2.out"
        }, "-=0.6"); // Start 0.6s before the end of project animation
      }

      if (projMeta.length) {
        projectTimeline.to(projMeta, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out"
        }, "-=0.6"); // Start 0.6s before the end of project animation
      }

      // Track if this project has been animated
      let projectAnimated = false;

      // Entrance ScrollTrigger for this project
      const projectEntranceScrollTrigger = this.ScrollTrigger.create({
        trigger: proj,
        start: "top 85%",
        end: "bottom 85%",
        markers: false,

        onEnter: () => {
          if (!projectAnimated) {
            projectTimeline.restart();
            projectAnimated = true;
          }
        },

        refreshPriority: -1
      });

      // Reset ScrollTrigger for this project - when far above (200%)
      const projectResetScrollTrigger = this.ScrollTrigger.create({
        trigger: proj,
        start: "top 200%", // 200% above the element
        end: "top 100%",   // Until element is 100% above
        markers: false,

        onEnterBack: () => {
          // Reset when scrolling back up to this far point
          this.gsap.set(proj, {
            opacity: 0,
            y: 50
          });

          // Reset gallery if it exists
          if (projGallery.length) {
            this.gsap.set(projGallery, {
              opacity: 0,
              y: 30
            });
          }

          projectAnimated = false; // Allow re-animation
        },

        refreshPriority: -2
      });

      // Store project ScrollTriggers
      this.projectScrollTriggers.push(projectEntranceScrollTrigger, projectResetScrollTrigger);
      this.timelines[`projectMobile_${index}`] = projectTimeline;
    });
  }

  // ========================================
  // END MOBILE/TABLET ANIMATIONS
  // ========================================



  init() {

    if (this.isInitialized) {
      this.destroy();
    }

    // console.log("Initializing MEC Animations");

    // Cache DOM elements
    this.cacheElements();

    // Check if required elements exist
    if (!this.elements.heroSection && !this.elements.projectsSection) {
      // console.log("No animation elements found, skipping initialization");
      return;
    }

    // Register GSAP plugins
    this.gsap.registerPlugin(this.ScrollTrigger, this.ScrollSmoother, this.SplitText);

    // Initialize Lenis
    this.initLenis();

    // Set ScrollTrigger defaults
    this.initScrollTriggerDefaults();

    // Handle page visibility changes to prevent performance issues
    this.handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - pause everything
        this.pauseAnimations();
        if (this.lenis) {
          this.lenis.stop();
          // Store current scroll position
          this.lastScrollPosition = this.lenis.scroll;
        }
      } else {
        // Page visible again - restore and refresh everything
        setTimeout(() => {
          if (this.lenis) {
            // Restore scroll position
            if (this.lastScrollPosition !== undefined) {
              this.lenis.scrollTo(this.lastScrollPosition, { immediate: true });
            }
            this.lenis.start();
          }

          // Force refresh ScrollTrigger calculations
          this.ScrollTrigger.refresh();

          // Resume animations
          this.resumeAnimations();

          // console.log("Page refocused - animations restored");
        }, 100);
      }
    };

    // document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Disable scroll initially
    document.body.style.overflow = "hidden";

    // 🎯 **IMPLEMENT GSAP MATCHMEDIA** 🎯
    this.matchMedia = this.gsap.matchMedia();

    // Desktop animations (1025px and up)
    this.matchMedia.add("(min-width: 1025px)", () => {
      // console.log("🖥️ Desktop view - Full animations enabled");

      const initDesktopAnimations = () => {
        this.initHeroAnimations();
        this.initSecondHeroAnimations();
        this.initCategoriesSectionAnimations();
        this.initBioAnimations();
        this.initProjectsAnimations();
        // console.log("Desktop MEC Animations loaded");
      };

      if (document.readyState === 'complete') {
        initDesktopAnimations();
      } else {
        window.addEventListener("load", initDesktopAnimations, { once: true });
      }

      // Return cleanup function for when this media query no longer matches
      return () => {
        // console.log("Leaving desktop view - cleaning up desktop animations");
        // Cleanup will be handled by destroy() method
      };
    });

    // Tablet animations (769px to 1024px)
    this.matchMedia.add("(min-width: 769px) and (max-width: 1024px)", () => {
      // console.log("📱 Tablet view - Simplified animations");

      const initTabletAnimations = () => {
        // this.initMobileHeroAnimations(); // Use mobile versions for tablets too
        // this.initMobileSecondHeroAnimations();
        // this.initMobileBioAnimations();
        // this.initMobileProjectsAnimations();

        this.initHeroAnimations();
        this.initSecondHeroAnimations();
        this.initBioAnimations();
        this.initProjectsAnimations();

        // console.log("Tablet MEC Animations loaded");
      };

      if (document.readyState === 'complete') {
        initTabletAnimations();
      } else {
        window.addEventListener("load", initTabletAnimations, { once: true });
      }

      return () => {
        // console.log("Leaving tablet view");
      };
    });

    // Mobile animations (768px and below)
    this.matchMedia.add("(max-width: 768px)", () => {
      // console.log("📱 Mobile view - Minimal animations");

      const initMobileAnimations = () => {
        this.initMobileHeroAnimations();
        this.initMobileSecondHeroAnimations();
        this.initMobileBioAnimations();
        this.initMobileProjectsAnimations();
        // console.log("Mobile MEC Animations loaded");
      };

      if (document.readyState === 'complete') {
        initMobileAnimations();
      } else {
        window.addEventListener("load", initMobileAnimations, { once: true });
      }

      return () => {
        // console.log("Leaving mobile view");
      };
    });

    this.isInitialized = true;
  }

  destroy() {
    if (!this.isInitialized) return;

    // console.log("Destroying MEC Animations");

    // Clean up bio mobile animations
    if (this.bioMobileAnimations) {
      this.bioMobileAnimations.forEach((animation) => {
        if (animation && typeof animation.kill === 'function') {
          animation.kill();
        }
      });
      this.bioMobileAnimations.clear();
      this.bioMobileAnimations = null;
    }

    // Clean up individual bio ScrollTriggers
    if (this.bioScrollTriggers) {
      this.bioScrollTriggers.forEach(trigger => {
        if (trigger && typeof trigger.kill === 'function') {
          trigger.kill();
        }
      });
      this.bioScrollTriggers = [];
    }

    // Clean up individual project ScrollTriggers
    if (this.projectScrollTriggers) {
      this.projectScrollTriggers.forEach(trigger => {
        if (trigger && typeof trigger.kill === 'function') {
          trigger.kill();
        }
      });
      this.projectScrollTriggers = [];
    }

    // Kill matchMedia instance first
    if (this.matchMedia) {
      this.matchMedia.kill();
      this.matchMedia = null;
    }

    // Remove ticker callback properly
    if (this.lenisTickerCallback) {
      this.gsap.ticker.remove(this.lenisTickerCallback);
      this.lenisTickerCallback = null;
    }

    // Kill all ScrollTriggers with force
    this.ScrollTrigger.killAll(true); // ← Add true for immediate kill

    // Kill all timelines including new mobile ones
    Object.values(this.timelines).forEach(tl => {
      if (Array.isArray(tl)) {
        // Handle hover timelines array
        tl.forEach(hoverData => {
          if (hoverData.timeline) {
            hoverData.timeline.kill();
            hoverData.timeline = null; // ← Clear reference
          }
          if (hoverData.element) {
            hoverData.element.removeEventListener('mouseenter', hoverData.onMouseEnter);
            hoverData.element.removeEventListener('mouseleave', hoverData.onMouseLeave);
          }
        });
      } else if (tl && typeof tl.kill === 'function') {
        tl.kill();
      }
    });

    // Destroy Lenis properly
    if (this.lenis) {
      this.lenis.off('scroll', this.throttledScrollUpdate);
      this.lenis.destroy();
      this.lenis = null;
    }

    // Remove visibility listener
    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.handleVisibilityChange = null;
    }

    // Clear throttled update timeout
    if (this.throttledScrollUpdate) {
      this.throttledScrollUpdate = null;
    }

    // Clear stored scroll position
    this.lastScrollPosition = undefined;

    // Clear all references
    this.elements = {};
    this.timelines = {};
    this.scrollTriggers = [];
    this.isInitialized = false;

    // Reset styles
    document.body.style.overflow = "auto";

    // Force one final ScrollTrigger refresh to clean up
    this.ScrollTrigger.refresh();
  }

  // Public methods for external control
  pauseAnimations() {
    // console.log("Pausing animations");

    // Pause all timelines
    Object.values(this.timelines).forEach(tl => {
      if (Array.isArray(tl)) {
        tl.forEach(item => {
          if (item.timeline && typeof item.timeline.pause === 'function') {
            item.timeline.pause();
          }
        });
      } else if (tl && typeof tl.pause === 'function') {
        tl.pause();
      }
    });

    // Pause GSAP ticker to save CPU
    this.gsap.ticker.lagSmoothing(0);
  }

  resumeAnimations() {
    // console.log("Resuming animations");

    // Resume all timelines
    Object.values(this.timelines).forEach(tl => {
      if (Array.isArray(tl)) {
        tl.forEach(item => {
          if (item.timeline && typeof item.timeline.resume === 'function') {
            item.timeline.resume();
          }
        });
      } else if (tl && typeof tl.resume === 'function') {
        tl.resume();
      }
    });

    // Restore GSAP ticker
    this.gsap.ticker.lagSmoothing(0);
  }

  refresh() {
    // console.log("Refreshing ScrollTrigger");

    // Force recalculate all ScrollTrigger positions
    this.ScrollTrigger.refresh();

    // Also refresh Lenis
    if (this.lenis) {
      this.lenis.resize();
    }
  }

  resize() {
    // console.log("Resizing MEC Animations");

    // Refresh ScrollTrigger on resize
    this.refresh();

    // Resize any specific elements if needed
    if (this.elements.heroSection) {
      this.gsap.set(this.elements.heroSection, { height: window.innerHeight });
    }

    // Resize Lenis if initialized
    if (this.lenis) {
      this.lenis.resize();
    }
  }
}